import './loanDocumentMileagePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { FeatureFlag } from '../../../store/featureStore/enums/FeatureFlag';
import { useFeatureFlag } from '../../../store/featureStore/hooks/useFeatureFlag';
import useLoanDocumentStore from '../../../store/loanDocumentStore/useLoanDocumentStore';
import { useNavigateWithCallback } from '../../../store/routeStore/hooks/useNavigateWithCallback';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();
  const navigateWithCallback = useNavigateWithCallback();

  const { t } = useTranslation();

  const isSimplifiedWorkflowEnabled = useFeatureFlag(
    FeatureFlag.ENABLE_SIMPLIFIED_LOAN_DOCUMENTS_WORKFLOW,
  );

  const {
    clearAllDocuments,
    isUploadFinished,
    isUploadSuccess,
    isUploading,
    mileage,
    setMileage,
    uploadDocuments,
    uploadFailedMessage,
  } = useLoanDocumentStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    defaultValues: {
      mileageValue: mileage,
    },
    delayError: 200,
  });

  const leadingZeroValidation = (
    value: string,
    error: string,
  ): string | boolean => {
    if (value.length > 1 && value.startsWith('0')) {
      return error;
    }
  };

  const mileageValidation = {
    required: t('validation.mileage.required'),
    validate: (value: string): string | boolean =>
      leadingZeroValidation(value, t('validation.mileage.invalid')),
  };

  useEffect((): void => {
    if (isUploadFinished && isUploadSuccess) {
      navigateWithCallback('/loan-documents/success').then((): void => {
        clearAllDocuments();
      });
    }
  }, [isUploadFinished, isUploadSuccess]);

  const previousStep = (): void => {
    if (isSimplifiedWorkflowEnabled) {
      navigate('/loan-documents/document-upload');
      return;
    }

    navigate('/loan-documents/list');
  };

  const formSubmit = (values: FieldValues): Promise<void> => {
    const { mileageValue } = values;

    if (!mileageValue) {
      return;
    }

    setMileage(mileageValue);

    uploadDocuments();
  };

  return (
    <div className="loan-document-mileage-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header progress={90} showDrawerToggle onBackClick={previousStep} />

          <main>
            <form
              data-testid={locator.pages.loanDocumentMileage.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentMileage.form.h1}>
                  {t('LoanDocumentMileagePage.title')}
                </h1>
                <h2 data-testid={locator.pages.loanDocumentMileage.form.h2}>
                  {t('LoanDocumentMileagePage.subtitle')}
                </h2>

                {!isUploading && !isEmpty(uploadFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={
                        locator.pages.loanDocumentMileage.alert.error
                      }
                      icon={false}
                      severity="error"
                    >
                      {uploadFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  <TextField
                    {...register('mileageValue', mileageValidation)}
                    error={!isEmpty(errors.mileageValue)}
                    label={t('LoanDocumentMileagePage.mileage')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.loanDocumentMileage.form.mileageInput,
                        inputMode: 'numeric',
                        min: 0,
                      },
                    }}
                    type="number"
                    variant="outlined"
                    autoFocus
                    fullWidth
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.mileageValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.loanDocumentMileage.form.mileageInputError
                      }
                    >
                      {toString(errors.mileageValue.message)}
                    </span>
                  )}
                </div>

                <Button
                  data-testid={
                    locator.pages.loanDocumentMileage.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isUploading}
                  loaderPosition="start"
                  fullWidth
                  submit
                >
                  {t('LoanDocumentMileagePage.submit')}
                </Button>
              </div>
              <footer
                data-testid={locator.pages.loanDocumentMileage.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentMileagePage';
